import { MedplumClient, createReference, getReferenceString, resolveId } from '@medplum/core';
import { HumanName, Patient, Reference, RelatedPerson } from '@medplum/fhirtypes';
import { canSearchResourceType } from '@/services/permissions';
import { RelatedContactFormData } from './forms/types';

function getHumanNameFromText(nameText: string): HumanName {
  const nameParts = nameText.split(' ');
  return {
    text: nameText,
    family: nameParts.pop(),
    given: nameParts.length > 0 ? nameParts : undefined,
  };
}

export async function createRelatedPerson(
  medplum: MedplumClient,
  params: {
    patient: Patient;
    contactData: RelatedContactFormData;
  }
): Promise<RelatedPerson> {
  const { patient, contactData } = params;

  return medplum.createResource<RelatedPerson>({
    resourceType: 'RelatedPerson',
    patient: createReference(patient),
    name: [getHumanNameFromText(contactData.name)],
    relationship: [
      {
        coding: [contactData.relationship],
      },
    ],
    ...(contactData.telecom && contactData.telecom.length > 0 && { telecom: contactData.telecom }),
  });
}

export async function updateRelatedPerson(
  medplum: MedplumClient,
  params: {
    relatedPersonId: string;
    contactData: RelatedContactFormData;
  }
): Promise<RelatedPerson> {
  const { relatedPersonId, contactData } = params;

  const relatedPerson = (await medplum.readResource('RelatedPerson', relatedPersonId)) as RelatedPerson;

  return medplum.updateResource<RelatedPerson>({
    ...relatedPerson,
    name: [getHumanNameFromText(contactData.name)],
    relationship: [
      {
        coding: [contactData.relationship],
      },
    ],
    telecom: contactData.telecom !== undefined ? contactData.telecom : (relatedPerson.telecom ?? []),
  });
}

export async function deleteRelatedPerson(medplum: MedplumClient, relatedPersonId: string): Promise<void> {
  await medplum.deleteResource('RelatedPerson', relatedPersonId);
}

export async function removeDeletedOrganizationFromPatientContact(
  patient: Patient,
  updatePatient: (updates: Partial<Patient>) => Promise<void>,
  organizationRef: Reference
): Promise<void> {
  const organizationRefString = getReferenceString(organizationRef);
  const updatedContacts = (patient.contact || []).filter((c) => {
    // Keep contacts that don't have an organization, or have a different organization reference
    return !c.organization?.reference || getReferenceString(c.organization) !== organizationRefString;
  });

  await updatePatient({
    contact: updatedContacts,
  });
}

/**
 * The RelatedPersons the patient's Coverages name as subscriber: insurance policyholders, which
 * bariendo-be and the `update-insurance-coverage` bot write. Any status, since a lapsed
 * Coverage still points at its policyholder. Undefined when it can't be told, because the
 * role can't search Coverage or the read failed (BAR-2967).
 */
export async function readPolicyholderIds(
  medplum: MedplumClient,
  patient: Patient | Reference<Patient>
): Promise<Set<string> | undefined> {
  if (!canSearchResourceType(medplum, 'Coverage')) return undefined;
  try {
    const coverages = await medplum.searchResources(
      'Coverage',
      { beneficiary: getReferenceString(patient), _count: '100' },
      { cache: 'no-cache' }
    );
    const ids = coverages
      .map((coverage) => coverage.subscriber)
      .filter((subscriber) => subscriber?.reference?.startsWith('RelatedPerson/'))
      .map((subscriber) => resolveId(subscriber));
    return new Set(ids.filter((id): id is string => Boolean(id)));
  } catch {
    return undefined;
  }
}

/** What the signed-in role can write, as Related contacts needs it. */
export interface ContactPermissions {
  updateRelatedPerson: boolean;
  deleteRelatedPerson: boolean;
  /** Partners live on `Patient.contact`, so removing one is a Patient update. */
  updatePatient: boolean;
}

export interface ContactActions {
  edit: boolean;
  remove: boolean;
  policyholder: boolean;
}

/**
 * What a Related contacts row offers. A policyholder offers nothing: a delete here leaves the
 * Coverage's subscriber pointing at nothing, losing the name and birth date prior auth needs,
 * and an edit rewrites the name and relationship outside the Coverage bot, with no Provenance
 * (BAR-2967). Neither does a related person while `policyholderIds` is unknown for it.
 * Everything else follows the role's write access, so a role that can't write sees no button
 * that only fails on the server (BAR-2968).
 */
export function contactActions(
  contact: { relatedPersonId?: string; organization?: Reference },
  policyholderIds: Set<string> | undefined,
  can: ContactPermissions
): ContactActions {
  if (!contact.relatedPersonId) {
    return { edit: false, remove: can.updatePatient, policyholder: false };
  }
  if (!policyholderIds) return { edit: false, remove: false, policyholder: false };
  if (policyholderIds.has(contact.relatedPersonId)) return { edit: false, remove: false, policyholder: true };
  return { edit: can.updateRelatedPerson, remove: can.deleteRelatedPerson, policyholder: false };
}
